import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";

const usePendingDevices = ({ storeId, page = 1, pageSize = 10 } = {}) => {
  const { request } = useApi();
  const [pendingDevices, setPendingDevices] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPendingDevices = useCallback(
    async (signal) => {
      if (!storeId) {
        setPendingDevices([]);
        setTotal(0);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await request({
          path: "/shipment/pending-devices",
          params: { page, pageSize },
          headers: { "X-Store-Id": storeId },
          signal,
        });

        setPendingDevices(
          Array.isArray(data?.pendingDevices) ? data.pendingDevices : []
        );
        setTotal(Number.isFinite(data?.total) ? data.total : 0);
      } catch (err) {
        if (err?.name === "AbortError") return;
        setPendingDevices([]);
        setTotal(0);
        setError(err?.message || "Failed to load pending devices.");
      } finally {
        setIsLoading(false);
      }
    },
    [page, pageSize, request, storeId]
  );

  useEffect(() => {
    if (!storeId) return;
    const controller = new AbortController();
    fetchPendingDevices(controller.signal);
    return () => controller.abort();
  }, [fetchPendingDevices, storeId]);

  const refetch = useCallback(() => {
    const controller = new AbortController();
    fetchPendingDevices(controller.signal);
    return () => controller.abort();
  }, [fetchPendingDevices]);

  const removeByIds = useCallback((ids = []) => {
    if (!ids.length) return;
    setPendingDevices((current) =>
      current.filter((device) => !ids.includes(device.id))
    );
  }, []);

  return { pendingDevices, total, isLoading, error, refetch, removeByIds };
};

export default usePendingDevices;
